import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiArrowRight, HiCheck, HiArrowLeft } from 'react-icons/hi';
import CustomButton from '../../../components/common/CustomButton';
import { ActiveIntegration, integrationData, getIntegrationType } from '../../../pages/Settings/Integrations/Integrations.types';
import { useIntegrationStatus } from '../../../hooks/useIntegrationStatus';

const CADENCE_LOGO = "https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp";

interface IntegrationGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectIntegration?: (id: ActiveIntegration) => void;
  initialStep?: number;
  singleStep?: boolean;
  triggerFunctionOnCustomStep?: { [key: number]: () => void };
}

const steps = [
  {
    id: 'intro',
    title: 'Seamless Integrations',
    description: 'Connect Cadence Pro with your favorite tools to centralize your project management and eliminate data silos.',
  },
  {
    id: 'sync',
    title: 'Real-time Synchronization',
    description: 'Automatically pull tasks, epics, and updates from external platforms directly into CadencePro.',
  },
  {
    id: 'collaboration',
    title: 'Unified Collaboration',
    description: 'Keep your entire team aligned with automated notifications and unified status tracking across all your tools.',
  },
  {
    id: 'select',
    title: 'Ready to Connect?',
    description: 'Select an integration below to get started and supercharge your workflow.',
  }
];

const IntegrationAnimation = ({ step }: { step: number }) => {
  if (step === 3) return null;

  return (
    <div className="relative w-64 h-64 flex items-center justify-center">
      {/* Background Glow */}
      <motion.div
        className="absolute w-48 h-48 bg-blue-500/10 rounded-full blur-[40px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Satellite Logos */}
      <AnimatePresence>
        {integrationData.map((logo, index) => {
          const angle = (index / integrationData.length) * (2 * Math.PI);
          const radius = 90;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;

          return (
            <motion.div
              key={logo.name}
              className="absolute w-12 h-12 bg-white rounded-xl shadow-lg border border-gray-100 dark:border-slate-700 p-2 flex items-center justify-center z-10"
              initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                x: step === 0 ? x : (step === 1 ? x * 0.4 : x),
                y: step === 0 ? y : (step === 1 ? y * 0.4 : y),
                rotate: step === 0 ? 0 : 360
              }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.1,
                ease: "backOut",
                rotate: { duration: 20, repeat: Infinity, ease: "linear" }
              }}
            >
              <img src={logo.logo} alt={logo.name} className="w-full h-full object-contain" />
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Connection Lines (Visible in Sync Step) */}
      {step === 1 && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible">
          {integrationData.map((_, index) => {
            const angle = (index / integrationData.length) * (2 * Math.PI);
            const radius = 90;
            const x1 = 128 + Math.cos(angle) * radius * 0.4;
            const y1 = 128 + Math.sin(angle) * radius * 0.4;
            return (
              <motion.line
                key={`line-${index}`}
                x1={x1}
                y1={y1}
                x2="128"
                y2="128"
                stroke="currentColor"
                className="text-blue-400/30"
                strokeWidth="2"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              />
            );
          })}
        </svg>
      )}

      {/* Center Logo */}
      <motion.div
        className="relative z-20 w-20 h-20 bg-white rounded-2xl shadow-2xl p-4 border-2 border-blue-500/20"
        animate={{
          scale: step === 1 ? [1, 1.1, 1] : 1,
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <motion.img
          src={CADENCE_LOGO}
          className="w-full h-full object-contain"
          alt="Cadence Pro"
          animate={{ rotate: step === 2 ? 360 : 0 }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        />
      </motion.div>
    </div>
  );
};

export const IntegrationGuideModal: React.FC<IntegrationGuideModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelectIntegration,
  initialStep = 0,
  singleStep = false,
  triggerFunctionOnCustomStep
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const { isIntegrationActive } = useIntegrationStatus();

  const anyIntegrationConnected = integrationData.some(integration => 
    isIntegrationActive(getIntegrationType(integration.id))
  );

  useEffect(() => {
    if (isOpen && triggerFunctionOnCustomStep && triggerFunctionOnCustomStep[currentStep]) {
      triggerFunctionOnCustomStep[currentStep]();
    }
  }, [currentStep, isOpen, triggerFunctionOnCustomStep]);

  useEffect(() => {
    if (isOpen) setCurrentStep(initialStep);
  }, [isOpen, initialStep]);

  const handleNext = () => {
    if (singleStep) {
      onClose();
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSelect = (id: ActiveIntegration) => {
    onSelectIntegration?.(id);
    // onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col min-h-[580px]"
      >
        <div className="flex-1 flex flex-col items-center justify-center p-10 text-center relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center w-full"
            >
              {/* Animation/Selection Section */}
              <div className="mb-8 flex items-center justify-center w-full">
                {currentStep != 3 && (
                  <IntegrationAnimation step={currentStep} />
                )}
              </div>

              {/* Text Content */}
              <div className="max-w-md">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                  {steps[currentStep].title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {steps[currentStep].description}
                </p>
              </div>


              <div className="py-4">
                {currentStep === 3 && (
                  <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
                    {integrationData.map((integration) => {
                      const isActive = isIntegrationActive(getIntegrationType(integration.id));
                      return (
                        <motion.button
                          key={integration.id}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleSelect(integration.id)}
                          className="group relative bg-gray-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-gray-100 dark:border-slate-700 hover:border-blue-500/50 hover:shadow-lg transition-all flex flex-col items-center gap-2"
                        >
                          <div className="w-12 h-12 bg-white rounded-xl p-2 shadow-sm">
                            <img src={integration.logo} alt={integration.name} className="w-full h-full object-contain" />
                          </div>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {integration.name}
                          </span>
                          {isActive && (
                            <motion.div 
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="absolute -top-2 -right-2 h-6 w-6 bg-green-500 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center shadow-md z-20"
                            >
                              <HiCheck className="text-white text-sm" />
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/30 dark:bg-slate-900/30 backdrop-blur-md">
          <div className="w-1/4">
            {currentStep > 0 && !singleStep && (
              <CustomButton
                variant="secondary"
                onClick={handleBack}
                className="flex items-center gap-2 group"
              >
                <HiArrowLeft className="text-lg group-hover:-translate-x-1 transition-transform" />
                Back
              </CustomButton>
            )}
          </div>

          <div className="flex gap-2 items-center">
            {steps.map((_, idx) => (
              <motion.div
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentStep
                    ? "w-8 bg-blue-600 dark:bg-blue-500"
                    : "w-2 bg-gray-200 dark:bg-slate-700"
                }`}
                animate={{
                  scale: idx === currentStep ? [1, 1.1, 1] : 1,
                }}
              />
            ))}
          </div>

          <div className="w-1/4 flex justify-end">
            <CustomButton
              onClick={handleNext}
              className="px-6 flex items-center gap-2 group shadow-lg shadow-blue-500/20"
            >
              {currentStep === steps.length - 1 || singleStep ? (
                <>
                  {singleStep ? "Next" : (anyIntegrationConnected ? "Finish" : "Skip")} <HiCheck className="text-xl" />
                </>
              ) : (
                <>
                  Next <HiArrowRight className="text-xl group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </CustomButton>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default IntegrationGuideModal;
